'use client';

import React, { useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import UserManual from '@/components/layout/UserManual';
import { BannerDisplay } from '@/components/layout/BannerDisplay';

interface SavingsResult {
  finalAmount: number;
  totalContributions: number;
  totalInterest: number;
  monthlyBreakdown: Array<{
    month: number;
    startBalance: number;
    contribution: number;
    interest: number;
    endBalance: number;
  }>;
}

const SavingsCalculator: React.FC = () => {
  const t = useTranslations('savings');
  const commonT = useTranslations('common');
  const [initialDeposit, setInitialDeposit] = useState<number>(1000);
  const [monthlyContribution, setMonthlyContribution] = useState<number>(200);
  const [interestRate, setInterestRate] = useState<number>(3.5);
  const [savingsTerm, setSavingsTerm] = useState<number>(1);
  const [compoundFrequency, setCompoundFrequency] = useState<number>(12);
  const [result, setResult] = useState<SavingsResult | null>(null);

  // Add focus state for each input
  const [isInitialDepositFocused, setIsInitialDepositFocused] = useState(false);
  const [isMonthlyContributionFocused, setIsMonthlyContributionFocused] = useState(false);
  const [isInterestRateFocused, setIsInterestRateFocused] = useState(false);
  const [isSavingsTermFocused, setIsSavingsTermFocused] = useState(false);

  // Add touched state for each input
  const [initialDepositTouched, setInitialDepositTouched] = useState(false);
  const [monthlyContributionTouched, setMonthlyContributionTouched] = useState(false);
  const [interestRateTouched, setInterestRateTouched] = useState(false);
  const [savingsTermTouched, setSavingsTermTouched] = useState(false);

  const calculateSavings = () => {
    // Convert years to months for calculations
    const totalMonths = savingsTerm * 12;
    const monthlyContributionAmount = monthlyContribution;

    let balance = initialDeposit;
    const monthlyBreakdown = [];

    for (let month = 1; month <= totalMonths; month++) {
      const startBalance = balance;
      
      // Add monthly contribution
      balance += monthlyContributionAmount;

      // Calculate interest only when it's time to compound
      // For annual: month % 12 === 0
      // For semi-annual: month % 6 === 0
      // For quarterly: month % 3 === 0
      // For monthly: every month
      const monthsPerPeriod = 12 / compoundFrequency;
      let interest = 0;
      
      if (month % monthsPerPeriod === 0) {
        // When it's time to compound, apply the appropriate periodic rate
        // For annual: full annual rate
        // For semi-annual: half the annual rate
        // For quarterly: quarter of the annual rate
        // For monthly: one-twelfth of the annual rate
        interest = balance * (interestRate / 100);
        if (compoundFrequency > 1) {
          interest = balance * (interestRate / 100 / compoundFrequency);
        }
      }
      
      balance += interest;

      monthlyBreakdown.push({
        month,
        startBalance,
        contribution: monthlyContributionAmount,
        interest,
        endBalance: balance,
      });
    }

    const totalContributions = initialDeposit + (monthlyContribution * totalMonths);
    const totalInterest = balance - totalContributions;

    setResult({
      finalAmount: balance,
      totalContributions,
      totalInterest,
      monthlyBreakdown,
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const manualSteps = [
    {
      label: t('manual.steps.set_initial.title'),
      content: () => t('manual.steps.set_initial.description')
    },
    {
      label: t('manual.steps.plan_contributions.title'),
      content: () => t('manual.steps.plan_contributions.description')
    },
    {
      label: t('manual.steps.enter_rate.title'),
      content: () => t('manual.steps.enter_rate.description')
    },
    {
      label: t('manual.steps.choose_period.title'),
      content: () => t('manual.steps.choose_period.description')
    },
    {
      label: t('manual.steps.select_frequency.title'),
      content: () => t('manual.steps.select_frequency.description')
    }
  ];

  const manualTips = [
    t('manual.tips.frequency'),
    t('manual.tips.regular_contributions'),
    t('manual.tips.monthly_breakdown'),
    t('manual.tips.inflation')
  ].map(tip => ({
    title: '',
    description: tip
  }));

  return (
    <Container maxWidth="lg">
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          <TextField
            fullWidth
            label={t('initial_deposit')}
            type="number"
            value={isInitialDepositFocused && !initialDepositTouched ? '' : (isNaN(initialDeposit) ? '' : initialDeposit)}
            onFocus={() => setIsInitialDepositFocused(true)}
            onBlur={(e) => {
              setIsInitialDepositFocused(false);
              setInitialDepositTouched(false);
              const value = Number(e.target.value);
              if (!e.target.value) {
                setInitialDeposit(1000); // default
              } else if (value < 0) {
                setInitialDeposit(0);
              } else if (value > 1000000) {
                setInitialDeposit(1000000);
              } else {
                setInitialDeposit(value);
              }
            }}
            onChange={(e) => {
              setInitialDepositTouched(true);
              setInitialDeposit(e.target.value === '' ? NaN : Number(e.target.value));
            }}
            slotProps={{
              input: {
                startAdornment: '$',
                inputProps: {
                  min: 0,
                  max: 1000000,
                },
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                },
              },
            }}
          />
          <TextField
            fullWidth
            label={t('monthly_contribution')}
            type="number"
            value={isMonthlyContributionFocused && !monthlyContributionTouched ? '' : (isNaN(monthlyContribution) ? '' : monthlyContribution)}
            onFocus={() => setIsMonthlyContributionFocused(true)}
            onBlur={(e) => {
              setIsMonthlyContributionFocused(false);
              setMonthlyContributionTouched(false);
              const value = Number(e.target.value);
              if (!e.target.value) {
                setMonthlyContribution(200); // default
              } else if (value < 0) {
                setMonthlyContribution(0);
              } else if (value > 1000000) {
                setMonthlyContribution(1000000);
              } else {
                setMonthlyContribution(value);
              }
            }}
            onChange={(e) => {
              setMonthlyContributionTouched(true);
              setMonthlyContribution(e.target.value === '' ? NaN : Number(e.target.value));
            }}
            slotProps={{
              input: {
                startAdornment: '$',
                inputProps: {
                  min: 0,
                  max: 1000000,
                },
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                },
              },
            }}
          />
          <TextField
            fullWidth
            label={t('interest_rate')}
            type="number"
            value={isInterestRateFocused && !interestRateTouched ? '' : (isNaN(interestRate) ? '' : interestRate)}
            onFocus={() => setIsInterestRateFocused(true)}
            onBlur={(e) => {
              setIsInterestRateFocused(false);
              setInterestRateTouched(false);
              const value = Number(e.target.value);
              if (!e.target.value) {
                setInterestRate(3.5); // default
              } else if (value < 0) {
                setInterestRate(0);
              } else if (value > 100) {
                setInterestRate(100);
              } else {
                setInterestRate(value);
              }
            }}
            onChange={(e) => {
              setInterestRateTouched(true);
              setInterestRate(e.target.value === '' ? NaN : Number(e.target.value));
            }}
            slotProps={{
              input: {
                endAdornment: '%',
                inputProps: {
                  min: 0,
                  max: 100,
                },
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                },
              },
            }}
          />
          <TextField
            fullWidth
            label={t('savings_term')}
            type="number"
            value={isSavingsTermFocused && !savingsTermTouched ? '' : (isNaN(savingsTerm) ? '' : savingsTerm)}
            onFocus={() => setIsSavingsTermFocused(true)}
            onBlur={(e) => {
              setIsSavingsTermFocused(false);
              setSavingsTermTouched(false);
              const value = Number(e.target.value);
              if (!e.target.value) {
                setSavingsTerm(1); // default
              } else if (value < 1) {
                setSavingsTerm(1);
              } else if (value > 50) {
                setSavingsTerm(50);
              } else {
                setSavingsTerm(value);
              }
            }}
            onChange={(e) => {
              setSavingsTermTouched(true);
              setSavingsTerm(e.target.value === '' ? NaN : Number(e.target.value));
            }}
            slotProps={{
              input: {
                endAdornment: t('savings_term_unit', { default: 'years' }),
                inputProps: {
                  min: 1,
                  max: 50,
                },
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                },
              },
            }}
          />
          <FormControl fullWidth>
            <InputLabel>{t('compound_frequency')}</InputLabel>
            <Select
              value={compoundFrequency}
              label={t('compound_frequency')}
              onChange={(e) => setCompoundFrequency(Number(e.target.value))}
            >
              <MenuItem value={1}>{t('annually')}</MenuItem>
              <MenuItem value={2}>{t('semi_annually')}</MenuItem>
              <MenuItem value={4}>{t('quarterly')}</MenuItem>
              <MenuItem value={12}>{t('monthly')}</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ gridColumn: { xs: '1 / -1' } }}>
            <Button
              variant="contained"
              color="primary"
              onClick={calculateSavings}
              fullWidth
            >
              {commonT('calculate')}
            </Button>
          </Box>
        </Box>

        {result && (
          <Box sx={{ mt: 4 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('final_amount')}
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {formatCurrency(result.finalAmount)}
                  </Typography>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('total_contributions')}
                  </Typography>
                  <Typography variant="h4" color="secondary">
                    {formatCurrency(result.totalContributions)}
                  </Typography>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('total_interest')}
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {formatCurrency(result.totalInterest)}
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            <Paper sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ p: 2 }}>
                {t('monthly_breakdown')}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('month')}</TableCell>
                      <TableCell align="right">{t('start_balance')}</TableCell>
                      <TableCell align="right">{t('contribution')}</TableCell>
                      <TableCell align="right">{t('interest')}</TableCell>
                      <TableCell align="right">{t('end_balance')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.monthlyBreakdown.map((row) => (
                      <TableRow key={row.month}>
                        <TableCell>{row.month}</TableCell>
                        <TableCell align="right">{formatCurrency(row.startBalance)}</TableCell>
                        <TableCell align="right">{formatCurrency(row.contribution)}</TableCell>
                        <TableCell align="right">{formatCurrency(row.interest)}</TableCell>
                        <TableCell align="right">{formatCurrency(row.endBalance)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            <Box sx={{ mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>
                {t('savings_summary')}
              </Typography>
              <Typography variant="body1" gutterBottom>
                {t('growth_summary', {
                  percentage: formatPercentage((result.totalInterest / result.totalContributions) * 100),
                  years: savingsTerm
                })}
              </Typography>
              <Typography variant="body1">
                {t('rule_of_72', {
                  years: Math.round(72 / interestRate)
                })}
              </Typography>
            </Box>
          </Box>
        )}
      </Paper>
      <UserManual 
        steps={manualSteps}
        tips={manualTips}
      />
      <BannerDisplay pageType="savings_calculator" />
    </Container>
  );
};

export default SavingsCalculator; 